import { AxiosRequestConfig, isAxiosError } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import { SkillTemplate } from "../skill-templates/types";
import {
  AdminGlobalSkillResponse,
  AdminGlobalSkillsResponse,
  ArchivedSkillListResponse,
  CreateSkillImportRequest,
  CreateSkillImportResponse,
  CreateSkillRequest,
  FinalizeSkillImportRequest,
  FinalizeSkillImportResponse,
  GetSkillFileResponse,
  ListSkillFilesResponse,
  PresignFileUploadRequest,
  PresignFileUploadResponse,
  PresignedSkillFileUpload,
  PutSkillRequest,
  Skill,
  SkillFile,
  SkillsResponse,
} from "./types";

export * from "./types";

/**
 * Thrown by `restore` when the trashed skill's name has been reused by an active
 * skill. `suggestedName` is a dash-suffixed alternative the caller can offer as a
 * "Keep both" rename. Surfaced as a typed error (rather than a raw 409) so callers
 * branch on a stable type instead of depending on the HTTP error envelope
 * surviving transport / generic error flattening.
 */
export class SkillNameTakenError extends Error {
  readonly suggestedName: string;

  constructor(suggestedName: string) {
    super("A skill with that name already exists.");
    this.name = "SkillNameTakenError";
    this.suggestedName = suggestedName;
  }
}

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/skills",
  SHOW: "/api/v2/org/:organization_id/user/:user_id/skills/:skill_id",
  CREATE: "/api/v2/org/:organization_id/user/:user_id/skills",
  UPDATE: "/api/v2/org/:organization_id/user/:user_id/skills/:skill_id",
  DELETE: "/api/v2/org/:organization_id/user/:user_id/skills/:skill_id",
  RESTORE:
    "/api/v2/org/:organization_id/user/:user_id/skills/:skill_id/restore",
  PUBLISH:
    "/api/v2/org/:organization_id/user/:user_id/skills/:skill_id/publish",
  ARCHIVED: "/api/v2/org/:organization_id/user/:user_id/archived/skills",
  PRESIGN_FILE_UPLOAD:
    "/api/v2/org/:organization_id/user/:user_id/skills/:skill_id/presign-file-upload",
  IMPORTS: "/api/v2/org/:organization_id/user/:user_id/skill-imports",
  IMPORT_FINALIZE:
    "/api/v2/org/:organization_id/user/:user_id/skill-imports/:import_id/finalize",
  /** GET list; GET/DELETE single file use the same prefix + encoded path (server: `.../files/*file_path`). */
  LIST_FILES:
    "/api/v2/org/:organization_id/user/:user_id/skills/:skill_id/files",
  ADMIN_LIST: "/api/v2/admin/skills",
  ADMIN_SHOW: "/api/v2/admin/skills/:skill_id",
};

/**
 * Encode path segments for the catch-all `*file_path` segment after `.../files/`.
 */
function encodeSkillFilePathSuffix(filePath: string): string {
  return filePath
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function buildSkillFileUrl(listFilesBaseUrl: string, filePath: string): string {
  const suffix = encodeSkillFilePathSuffix(filePath);
  return suffix ? `${listFilesBaseUrl}/${suffix}` : listFilesBaseUrl;
}

export class SkillsAPI {
  constructor(private client: CohesiveAPIClient) {}

  async list(config?: AxiosRequestConfig): Promise<Skill[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<SkillsResponse>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
      }),
      config,
    );
    return response.data.data.skills;
  }

  async getById(id: string, config?: AxiosRequestConfig): Promise<Skill> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<{ data: { skill: Skill } }>(
      route(ENDPOINTS.SHOW, {
        organization_id: organizationId,
        user_id: userId,
        skill_id: id,
      }),
      config,
    );
    return response.data.data.skill;
  }

  async create(
    data: CreateSkillRequest,
    config?: AxiosRequestConfig,
  ): Promise<Skill> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<{ data: { skill: Skill } }>(
      route(ENDPOINTS.CREATE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data.skill;
  }

  async update(
    id: string,
    data: PutSkillRequest,
    config?: AxiosRequestConfig,
  ): Promise<Skill> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.put<{ data: { skill: Skill } }>(
      route(ENDPOINTS.UPDATE, {
        organization_id: organizationId,
        user_id: userId,
        skill_id: id,
      }),
      data,
      config,
    );
    return response.data.data.skill;
  }

  async delete(id: string, config?: AxiosRequestConfig): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        skill_id: id,
      }),
      config,
    );
  }

  /** The DELETE route archives (moves to Trash); restore brings it back.
   *  Pass new_name to rename the skill on restore (used by "Keep both" collision resolution). */
  async restore(
    skillId: string,
    body?: { new_name?: string },
    config?: AxiosRequestConfig,
  ): Promise<Skill> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    try {
      const response = await this.client.axios.post<{ data: { skill: Skill } }>(
        route(ENDPOINTS.RESTORE, {
          organization_id: organizationId,
          user_id: userId,
          skill_id: skillId,
        }),
        body ?? {},
        config,
      );
      return response.data.data.skill;
    } catch (e) {
      // The server returns 409 {error:"name_taken", suggested_name} when the
      // name was reused while the skill sat in Trash. Translate it here, where
      // the raw response is still available, into a typed error.
      if (isAxiosError(e) && e.response?.status === 409) {
        const data = e.response.data as {
          error?: string;
          suggested_name?: string;
        };
        if (data?.error === "name_taken") {
          throw new SkillNameTakenError(data.suggested_name ?? "");
        }
      }
      throw e;
    }
  }

  /** Lists archived (trashed) private skills, newest first (cursor-paginated by archived_at). */
  async listArchived(
    params?: { limit?: number; before_ts?: string },
    config?: AxiosRequestConfig,
  ): Promise<ArchivedSkillListResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<ArchivedSkillListResponse>(
      route(ENDPOINTS.ARCHIVED, {
        organization_id: organizationId,
        user_id: userId,
      }),
      { ...config, params: { ...config?.params, ...params } },
    );
    return response.data;
  }

  /** Publishes this private skill as an organization template. Returns the new template. */
  async publish(
    skillId: string,
    config?: AxiosRequestConfig,
  ): Promise<SkillTemplate> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<{
      data: { skill_template: SkillTemplate };
    }>(
      route(ENDPOINTS.PUBLISH, {
        organization_id: organizationId,
        user_id: userId,
        skill_id: skillId,
      }),
      {},
      config,
    );
    return response.data.data.skill_template;
  }

  /** Starts a multi-file skill import: validates the manifest server-side and
   *  returns presigned uploads into a staging area, keyed by root-relative path. */
  async createImport(
    data: CreateSkillImportRequest,
    config?: AxiosRequestConfig,
  ): Promise<CreateSkillImportResponse["data"]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<CreateSkillImportResponse>(
      route(ENDPOINTS.IMPORTS, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data.data;
  }

  /** Finalizes a staged import: the server parses the staged SKILL.md, creates
   *  the skill, and moves the files into it. 409 on a name conflict — retry
   *  with name_override. */
  async finalizeImport(
    importId: string,
    data?: FinalizeSkillImportRequest,
    config?: AxiosRequestConfig,
  ): Promise<FinalizeSkillImportResponse["data"]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<FinalizeSkillImportResponse>(
      route(ENDPOINTS.IMPORT_FINALIZE, {
        organization_id: organizationId,
        user_id: userId,
        import_id: importId,
      }),
      data ?? {},
      config,
    );
    return response.data.data;
  }

  async presignFileUpload(
    skillId: string,
    data: PresignFileUploadRequest,
    config?: AxiosRequestConfig,
  ): Promise<PresignedSkillFileUpload> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<PresignFileUploadResponse>(
      route(ENDPOINTS.PRESIGN_FILE_UPLOAD, {
        organization_id: organizationId,
        user_id: userId,
        skill_id: skillId,
      }),
      data,
      config,
    );
    return response.data.data;
  }

  async listFiles(
    skillId: string,
    config?: AxiosRequestConfig,
  ): Promise<SkillFile[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<ListSkillFilesResponse>(
      route(ENDPOINTS.LIST_FILES, {
        organization_id: organizationId,
        user_id: userId,
        skill_id: skillId,
      }),
      config,
    );
    return response.data.data.files;
  }

  async getFile(
    skillId: string,
    filePath: string,
    config?: AxiosRequestConfig,
  ): Promise<SkillFile> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const base = route(ENDPOINTS.LIST_FILES, {
      organization_id: organizationId,
      user_id: userId,
      skill_id: skillId,
    });
    const url = buildSkillFileUrl(base, filePath);
    const response = await this.client.axios.get<GetSkillFileResponse>(
      url,
      config,
    );
    return response.data.data.file;
  }

  async deleteFile(
    skillId: string,
    filePath: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const base = route(ENDPOINTS.LIST_FILES, {
      organization_id: organizationId,
      user_id: userId,
      skill_id: skillId,
    });
    const url = buildSkillFileUrl(base, filePath);
    await this.client.axios.delete(url, config);
  }

  // Admin methods for global skills
  async adminListGlobalSkills(config?: AxiosRequestConfig): Promise<Skill[]> {
    const response = await this.client.axios.get<AdminGlobalSkillsResponse>(
      ENDPOINTS.ADMIN_LIST,
      config,
    );
    return response.data.data.skills;
  }

  async adminGetGlobalSkill(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<Skill> {
    const response = await this.client.axios.get<AdminGlobalSkillResponse>(
      route(ENDPOINTS.ADMIN_SHOW, { skill_id: id }),
      config,
    );
    return response.data.data.skill;
  }
}
